// src/modules/Rules/components/display/RuleConditionsSection.tsx
// Sección del formulario para gestionar las condiciones de una regla de envío (campos, operadores, valores, lógica AND/OR).
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { faCircleInfo, faListCheck, faPlus, faSpinner, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { Switch } from '@shared/components/ui/shadcn/switch';
import React from 'react';
import { useTranslation } from 'react-i18next';
// Importamos el Combobox con buscador
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { useClientRuleConditionValueSelect } from '../../hooks/useClientRuleConditionValueSelect';
import { FormSectionCard } from './FormSectionCard';

export interface Condition {
  id: number;
  field: string;
  operator: string;
  value: string;
}

interface RuleConditionsSectionProps {
  conditions: Condition[];
  matchAll: boolean;
  onToggleMatchAll: (v: boolean) => void;
  conditionOptions: { id: string; name: string }[];
  loadOperators: (field: string) => Promise<{ id: string; name: string }[]>;
  loadValues: (field: string) => Promise<{
    options: { id: string; name: string }[];
    valueType: 'select' | 'text' | 'numeric';
  }>;
  onUpdateCondition: (id: number, key: keyof Condition, val: string) => void;
  onRemoveCondition: (id: number) => void;
  onAddCondition: () => void;
  courierIntegrations?: { id: number; name: string }[];
  loadCourierProducts?: (integrationId: string) => Promise<{ id: string; name: string }[]>;
  findCourierByServiceId?: (serviceId: string) => Promise<string | null>;
  /** Cliente (reglas en /clients/:id/rules): producto/tienda con búsqueda y paginación filtradas por team */
  clientTeamId?: number;
  isReadOnly?: boolean;
  sectionStyle?: 'default' | 'clientCard';
}

export const RuleConditionsSection: React.FC<RuleConditionsSectionProps> = ({
  conditions,
  matchAll,
  onToggleMatchAll,
  conditionOptions,
  loadOperators,
  loadValues,
  onUpdateCondition,
  onRemoveCondition,
  onAddCondition,
  courierIntegrations = [],
  loadCourierProducts,
  findCourierByServiceId,
  clientTeamId,
  isReadOnly = false,
  sectionStyle = 'default',
}) => {
  const { t } = useTranslation('rulesModule');

  // Memoización local para operadores y valores por campo
  const [operatorsCache, setOperatorsCache] = React.useState<Record<string, { id: string; name: string }[]>>({});
  const [valuesCache, setValuesCache] = React.useState<
    Record<
      string,
      {
        options: { id: string; name: string }[];
        valueType: 'select' | 'text' | 'numeric';
      }
    >
  >({});
  const [loadingFields, setLoadingFields] = React.useState<Record<string, boolean>>({});

  // Función memoizada para obtener operadores
  const getOperators = React.useCallback(
    async (field: string) => {
      if (operatorsCache[field]) return operatorsCache[field];
      setLoadingFields((prev) => ({ ...prev, [field]: true }));
      const ops = await loadOperators(field);
      setOperatorsCache((prev) => ({ ...prev, [field]: ops }));
      setLoadingFields((prev) => ({ ...prev, [field]: false }));
      return ops;
    },
    [operatorsCache, loadOperators],
  );

  // Función memoizada para obtener valores
  const getValues = React.useCallback(
    async (field: string) => {
      if (valuesCache[field]) return valuesCache[field];
      setLoadingFields((prev) => ({ ...prev, [field]: true }));
      const val = await loadValues(field);
      setValuesCache((prev) => ({ ...prev, [field]: val }));
      setLoadingFields((prev) => ({ ...prev, [field]: false }));
      return val;
    },
    [valuesCache, loadValues],
  );

  // Limpiar caché si cambian las condiciones (opcional, para evitar leaks)
  React.useEffect(() => {
    // Si quieres limpiar el caché cuando cambian las condiciones, descomenta:
    // setOperatorsCache({});
    // setValuesCache({});
    // setLoadingFields({});
  }, [conditions]);

  const inner = (
    <>
      {sectionStyle === 'default' && <h2 className="text-lg font-semibold mb-3">{t('create.conditions_title')}</h2>}

      {conditions.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <Switch checked={matchAll} onCheckedChange={onToggleMatchAll} disabled={isReadOnly} />
          <span>{matchAll ? t('create.match_all') : t('create.match_any')}</span>
        </div>
      )}

      {conditions.length === 0 && (
        <Alert variant="default" className="border-border bg-muted/30">
          <FontAwesomeIcon icon={faCircleInfo} className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden />
          <AlertTitle>{t('create.alert_title')}</AlertTitle>
          <AlertDescription>
            {isReadOnly ? t('create.alert_readonly_description') : t('create.alert_description')}
          </AlertDescription>
        </Alert>
      )}

      {conditions.map((condition) => (
        <ConditionRow
          key={condition.id}
          condition={condition}
          onUpdate={onUpdateCondition}
          onRemove={onRemoveCondition}
          loadOperators={getOperators}
          loadValues={getValues}
          conditionOptions={conditionOptions}
          courierIntegrations={courierIntegrations}
          loadCourierProducts={loadCourierProducts}
          findCourierByServiceId={findCourierByServiceId}
          clientTeamId={clientTeamId}
          t={t}
          loadingField={loadingFields[condition.field] || false}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && (
        <div className="flex justify-end mt-3">
          <Button variant="outline" size="sm" icon={faPlus} onClick={onAddCondition}>
            {t('create.add_condition')}
          </Button>
        </div>
      )}
    </>
  );

  if (sectionStyle === 'clientCard') {
    return (
      <FormSectionCard title={t('clientList.display.conditions')} icon={faListCheck}>
        {inner}
      </FormSectionCard>
    );
  }

  return <SectionBox>{inner}</SectionBox>;
};

interface ConditionRowProps {
  condition: Condition;
  onUpdate: (id: number, key: keyof Condition, val: string) => void;
  onRemove: (id: number) => void;
  loadOperators: (field: string) => Promise<{ id: string; name: string }[]>;
  loadValues: (field: string) => Promise<{
    options: { id: string; name: string }[];
    valueType: 'select' | 'text' | 'numeric';
  }>;
  conditionOptions: { id: string; name: string }[];
  courierIntegrations?: { id: number; name: string }[];
  loadCourierProducts?: (integrationId: string) => Promise<{ id: string; name: string }[]>;
  findCourierByServiceId?: (serviceId: string) => Promise<string | null>;
  clientTeamId?: number;
  t: ReturnType<typeof useTranslation>['t'];
  loadingField?: boolean;
  isReadOnly?: boolean;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  onUpdate,
  onRemove,
  loadOperators,
  loadValues,
  conditionOptions,
  courierIntegrations = [],
  loadCourierProducts,
  findCourierByServiceId,
  clientTeamId,
  t,
  loadingField = false,
  isReadOnly = false,
}) => {
  const clientScopedSelect = useClientRuleConditionValueSelect(clientTeamId, condition.field);
  const [availableOperators, setAvailableOperators] = React.useState<{ id: string; name: string }[]>([]);
  const [availableValues, setAvailableValues] = React.useState<{ id: string; name: string }[]>([]);
  const [valueType, setValueType] = React.useState<'select' | 'text' | 'numeric'>('text');
  const [loading, setLoading] = React.useState(false);

  // Estado para manejar la selección de courier y service cuando field es has_coverage
  const [selectedCourierId, setSelectedCourierId] = React.useState<string>('');
  const [courierServices, setCourierServices] = React.useState<{ id: string; name: string }[]>([]);
  const [loadingServices, setLoadingServices] = React.useState(false);
  const [loadingCourier, setLoadingCourier] = React.useState(false);

  // bandera única para deshabilitar operador y valor:
  const isDisabled = !condition.field || loading || loadingField || isReadOnly;

  const isHasCoverage = condition.field === 'has_coverage';

  // Ref para rastrear el campo anterior y evitar recargas innecesarias
  const prevFieldRef = React.useRef<string>(condition.field);

  React.useEffect(() => {
    if (!condition.field) {
      setAvailableOperators([]);
      setAvailableValues([]);
      setValueType('text');
      setSelectedCourierId('');
      setCourierServices([]);
      setLoadingCourier(false);
      prevFieldRef.current = '';
      return;
    }

    // Si es has_coverage, asegurar que el operador sea "="
    if (isHasCoverage && condition.operator !== '=') {
      onUpdate(condition.id, 'operator', '=');
    }

    // Si cambia el campo y no es has_coverage, resetear courier
    if (!isHasCoverage) {
      setSelectedCourierId('');
      setCourierServices([]);
      setLoadingCourier(false);
    }

    // Solo cargar operadores/valores si el campo cambió realmente
    if (prevFieldRef.current !== condition.field || availableOperators.length === 0) {
      setLoading(true);
      Promise.all([loadOperators(condition.field), loadValues(condition.field)])
        .then(([ops, { options, valueType }]) => {
          setAvailableOperators(ops);
          setAvailableValues(options);
          setValueType(valueType);
          prevFieldRef.current = condition.field;
        })
        .finally(() => setLoading(false));
    }
  }, [
    condition.field,
    condition.operator,
    loadOperators,
    loadValues,
    condition.id,
    isHasCoverage,
    onUpdate,
    availableOperators.length,
  ]);

  // Cuando se selecciona un courier, cargar sus servicios
  React.useEffect(() => {
    if (!isHasCoverage || !selectedCourierId || !loadCourierProducts) {
      return;
    }
    setLoadingServices(true);
    loadCourierProducts(selectedCourierId)
      .then((services) => {
        setCourierServices(services);
        // Si hay un valor guardado pero no coincide con ningún servicio del courier seleccionado, limpiarlo
        if (condition.value && !services.find((s) => s.id === condition.value)) {
          onUpdate(condition.id, 'value', '');
        }
      })
      .finally(() => setLoadingServices(false));
  }, [selectedCourierId, isHasCoverage, loadCourierProducts, condition.id, condition.value, onUpdate]);

  // Inicializar courier seleccionado cuando se carga una condición existente con has_coverage
  React.useEffect(() => {
    if (
      isHasCoverage &&
      condition.value &&
      courierIntegrations.length > 0 &&
      !selectedCourierId &&
      findCourierByServiceId
    ) {
      // Buscar el courier que tiene el servicio seleccionado
      setLoadingCourier(true);
      findCourierByServiceId(condition.value)
        .then((courierId) => {
          if (courierId) {
            setSelectedCourierId(courierId);
          }
        })
        .catch((error) => {
          console.error('Error al buscar courier por service ID:', error);
        })
        .finally(() => {
          setLoadingCourier(false);
        });
    }
  }, [isHasCoverage, condition.value, courierIntegrations.length, selectedCourierId, findCourierByServiceId]);

  // Mapeo al Combobox: título + descripción (i18n) por cada tipo de condición
  const fieldOptions = React.useMemo(() => {
    const fromCatalog = conditionOptions.map((o) => ({
      label: o.name,
      value: o.id,
      description: t(`create.condition_field_descriptions.${o.id}`),
    }));
    if (!condition.field || fromCatalog.some((o) => o.value === condition.field)) {
      return fromCatalog;
    }
    return [
      ...fromCatalog,
      {
        label: t(`clientList.display.fields.${condition.field}`, { defaultValue: condition.field }),
        value: condition.field,
        description: t('create.unlisted_field_description'),
      },
    ];
  }, [conditionOptions, condition.field, t]);
  const operatorOptions = availableOperators.map((o) => ({
    label: o.name,
    value: o.id,
  }));
  const valueOptions = availableValues.map((v) => ({
    label: v.name,
    value: v.id,
  }));

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4 border-b border-card-border sm:border-0 py-2 mb-3 sm:mb-0">
      <div className="grid min-w-0 flex-1 grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4">
        {/* Campo — 5/12 */}
        <div className="min-w-0 sm:col-span-5">
          <Combobox
            dataTestId={`field-${condition.id}`}
            options={fieldOptions}
            placeholder={t('create.select_condition')}
            value={condition.field}
            setValue={(val) => {
              onUpdate(condition.id, 'field', val);
              // Si es has_coverage, establecer automáticamente el operador a "="
              if (val === 'has_coverage') {
                onUpdate(condition.id, 'operator', '=');
              } else {
                onUpdate(condition.id, 'operator', '');
              }
              onUpdate(condition.id, 'value', '');
              // Resetear courier cuando cambia el campo
              if (val !== 'has_coverage') {
                setSelectedCourierId('');
                setCourierServices([]);
              }
            }}
            className="w-full"
            disabled={isReadOnly}
          />
        </div>

        {/* Operador — 3/12 */}
        {!isHasCoverage && (
          <div
            className={`min-w-0 sm:col-span-3 transition-opacity ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <Combobox
              dataTestId={`operator-${condition.id}`}
              options={operatorOptions}
              placeholder={t('create.select_operator')}
              value={condition.operator}
              setValue={(val) => onUpdate(condition.id, 'operator', val)}
              className="w-full"
              disabled={isDisabled}
            />
          </div>
        )}

        {/* Courier (has_coverage) — 3/12 */}
        {isHasCoverage && (
          <div
            className={`min-w-0 sm:col-span-3 transition-opacity ${isDisabled || loadingCourier ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <div className="relative">
              <Combobox
                dataTestId={`courier-${condition.id}`}
                options={courierIntegrations.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                }))}
                placeholder={loadingCourier ? t('loading.message') : t('create.select_courier')}
                value={selectedCourierId}
                setValue={(val) => {
                  setSelectedCourierId(val);
                  // Limpiar el valor del servicio cuando cambia el courier
                  onUpdate(condition.id, 'value', '');
                }}
                className="w-full"
                disabled={isDisabled || isReadOnly || loadingCourier}
              />
              {loadingCourier && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Valor o servicio — 4/12 */}
        {isHasCoverage ? (
          <div
            className={`min-w-0 sm:col-span-4 transition-opacity ${isDisabled || !selectedCourierId || loadingServices || courierServices.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <Combobox
              dataTestId={`service-${condition.id}`}
              options={courierServices.map((s) => ({
                label: s.name,
                value: s.id,
              }))}
              placeholder={
                loadingServices
                  ? t('loading.message')
                  : !selectedCourierId
                    ? t('create.select_courier_first')
                    : t('create.select_service')
              }
              value={condition.value}
              setValue={(val) => onUpdate(condition.id, 'value', val)}
              className="w-full"
              disabled={
                isDisabled || isReadOnly || !selectedCourierId || loadingServices || courierServices.length === 0
              }
            />
          </div>
        ) : (
          <div
            className={`min-w-0 sm:col-span-4 transition-opacity ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}
          >
            {valueType === 'select' &&
              (clientScopedSelect ? (
                <DynamicCombobox
                  dataTestId={`value-${condition.id}`}
                  value={condition.value}
                  setValue={(val) => onUpdate(condition.id, 'value', val)}
                  placeholder={t('create.select_value')}
                  searchFn={clientScopedSelect.searchFn}
                  hasMore={clientScopedSelect.hasMore}
                  resolveSelectedOption={clientScopedSelect.resolveSelected}
                  className="w-full"
                  disabled={isDisabled}
                  emptyMessage="No se encontraron resultados"
                  loadingMessage={t('loading.message')}
                />
              ) : (
                <Combobox
                  dataTestId={`value-${condition.id}`}
                  options={valueOptions}
                  placeholder={t('create.select_value')}
                  value={condition.value}
                  setValue={(val) => onUpdate(condition.id, 'value', val)}
                  className="w-full"
                  disabled={isDisabled}
                />
              ))}
            {valueType === 'text' && (
              <Input
                type="text"
                value={condition.value}
                onChange={(e) => onUpdate(condition.id, 'value', e.target.value)}
                placeholder={t('create.enter_value')}
                disabled={isDisabled}
              />
            )}
            {valueType === 'numeric' && (
              <Input
                type="number"
                value={condition.value}
                onChange={(e) => onUpdate(condition.id, 'value', e.target.value)}
                placeholder={t('create.enter_number')}
                disabled={isDisabled}
              />
            )}
          </div>
        )}
      </div>

      {!isReadOnly && (
        <div className="flex w-full shrink-0 justify-end sm:w-auto sm:pb-2">
          <Button variant="ghost" size="sm" onClick={() => onRemove(condition.id)} disabled={loading}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default RuleConditionsSection;
